import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { RefreshCw, Search, RotateCcw, Edit3, Check, X, Filter, Save, Settings2 } from "lucide-react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import usePagedRows from "../components/usePagedRows";
import Pagination from "../components/Pagination";

export default function Turnover() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [onlyPending, setOnlyPending] = useState(false);
  const [editing, setEditing] = useState(null);
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const paged = usePagedRows(rows, 12, [search, onlyPending]);

  const loadSettings = useCallback(async () => {
    try {
      const { data } = await api.get("/settings");
      setSettings({
        turnover_enabled: data.turnover_enabled !== false,
        turnover_registration_multiplier: data.turnover_registration_multiplier ?? 2,
        turnover_deposit_multiplier: data.turnover_deposit_multiplier ?? 2,
        turnover_referral_multiplier: data.turnover_referral_multiplier ?? 2,
      });
    } catch (e) {
      toast.error("Failed to load turnover settings");
    }
  }, []);

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.put("/settings", settings);
      toast.success("Turnover settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingSettings(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search.trim()) params.search = search.trim();
      if (onlyPending) params.only_pending = true;
      const { data } = await api.get("/turnover", { params });
      setRows(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load turnover data");
    } finally {
      setLoading(false);
    }
  }, [search, onlyPending]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  useEffect(() => {
    load();
  }, [load]);

  const handleReset = async (row) => {
    if (!window.confirm(`Reset turnover for ${row.username}? This will set both required and completed to 0.`)) return;
    try {
      await api.post(`/turnover/${row.id}/adjust`, { reset: true });
      toast.success(`Turnover reset for ${row.username}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    }
  };

  const handleSave = async (row) => {
    try {
      await api.post(`/turnover/${row.id}/adjust`, {
        turnover_required: parseFloat(editing.required || 0),
        turnover_completed: parseFloat(editing.completed || 0),
      });
      toast.success(`Turnover updated for ${row.username}`);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const setS = (k, v) => setSettings((s) => ({ ...s, [k]: v }));

  const pendingCount = rows.filter((r) => !r.can_withdraw).length;
  const eligibleCount = rows.filter((r) => r.can_withdraw).length;

  return (
    <div data-testid="admin-turnover-page">
      <PageHeader
        title="Turnover Management"
        subtitle="Control the wagering requirement · on/off + multipliers + per-player status"
      />

      {settings && (
        <div className="bg-white border border-gray-200 rounded-sm p-5 mb-5" data-testid="turnover-settings-panel">
          <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-100">
            <Settings2 size={14} className="text-emerald-600" />
            <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">Bonus Turnover Settings (legacy multipliers · default 2×)</div>
          </div>

          <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-sm cursor-pointer hover:bg-gray-50 mb-4">
            <input
              type="checkbox"
              checked={settings.turnover_enabled}
              onChange={(e) => setS("turnover_enabled", e.target.checked)}
              data-testid="turnover-enabled-toggle"
              className="h-4 w-4"
            />
            <div className="flex-1">
              <div className="text-sm font-semibold text-gray-900">
                {settings.turnover_enabled ? "Turnover requirement is ON" : "Turnover requirement is OFF"}
              </div>
              <div className="text-xs text-gray-500">
                {settings.turnover_enabled
                  ? "Users must wager the multiple of every bonus amount before they can withdraw."
                  : "Users can withdraw freely without any wagering requirement."}
              </div>
            </div>
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                settings.turnover_enabled
                  ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                  : "bg-gray-100 text-gray-600 border border-gray-200"
              }`}
            >
              {settings.turnover_enabled ? "ON" : "OFF"}
            </span>
          </label>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <SetField
              label="Registration Bonus ×"
              hint="e.g. 2× means ৳50 reg bonus requires ৳100 wagered"
              value={settings.turnover_registration_multiplier}
              onChange={(v) => setS("turnover_registration_multiplier", v)}
              testId="turnover-reg-mult-input"
              disabled={!settings.turnover_enabled}
            />
            <SetField
              label="Deposit Bonus ×"
              hint="e.g. 2× means ৳100 deposit bonus requires ৳200 wagered"
              value={settings.turnover_deposit_multiplier}
              onChange={(v) => setS("turnover_deposit_multiplier", v)}
              testId="turnover-dep-mult-input"
              disabled={!settings.turnover_enabled}
            />
            <SetField
              label="Referral Commission ×"
              hint="e.g. 2× means ৳50 commission requires ৳100 wagered"
              value={settings.turnover_referral_multiplier}
              onChange={(v) => setS("turnover_referral_multiplier", v)}
              testId="turnover-ref-mult-input"
              disabled={!settings.turnover_enabled}
            />
          </div>

          <div className="mt-3 p-2.5 bg-amber-50 border border-amber-200 rounded-sm text-[11px] text-amber-800">
            ℹ️ <b>Note:</b> The active rollover engine is the <b>dual-bucket V2 system</b> below (Deposit 2× + Bonus 2×). These legacy multipliers only apply to first-signup registration bonuses for backward compatibility.
          </div>

          <div className="flex justify-end mt-4">
            <button
              onClick={saveSettings}
              disabled={savingSettings}
              className="h-10 px-5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-sm text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50"
              data-testid="turnover-save-settings-btn"
            >
              <Save size={14} /> {savingSettings ? "Saving..." : "Save Turnover Settings"}
            </button>
          </div>
        </div>
      )}

      <AdvancedTurnover />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <StatCard label="Total players shown" value={rows.length} tone="slate" />
        <StatCard label="Pending turnover" value={pendingCount} tone="amber" />
        <StatCard label="Eligible to withdraw" value={eligibleCount} tone="emerald" />
      </div>

      <div className="bg-white border border-gray-200 rounded-sm p-4 mb-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[260px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search username or full name..."
            className="w-full h-10 pl-9 pr-3 border border-gray-200 rounded-sm text-sm"
            data-testid="turnover-search-input"
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer px-3 py-2 border border-gray-200 rounded-sm hover:bg-gray-50">
          <input
            type="checkbox"
            checked={onlyPending}
            onChange={(e) => setOnlyPending(e.target.checked)}
            data-testid="turnover-only-pending"
          />
          <Filter size={13} /> Only pending
        </label>
        <button
          onClick={load}
          disabled={loading}
          className="h-10 px-4 bg-slate-800 hover:bg-slate-900 text-white rounded-sm text-sm inline-flex items-center gap-2 disabled:opacity-50"
          data-testid="turnover-refresh-btn"
        >
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr className="text-left text-[11px] uppercase tracking-wider text-gray-500">
                <th className="px-4 py-3">Player</th>
                <th className="px-4 py-3 text-right">Balance</th>
                <th className="px-4 py-3 text-right">Required</th>
                <th className="px-4 py-3 text-right">Completed</th>
                <th className="px-4 py-3 text-right">Remaining</th>
                <th className="px-4 py-3">Progress</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.length === 0 && !loading && (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-sm text-gray-500">
                    No players found.
                  </td>
                </tr>
              )}
              {paged.visible.map((r) => {
                const isEditing = editing?.id === r.id;
                return (
                  <tr key={r.id} className="hover:bg-gray-50" data-testid={`turnover-row-${r.username}`}>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-gray-900">{r.username}</div>
                      <div className="text-xs text-gray-500">{r.full_name}</div>
                    </td>
                    <td className="px-4 py-3 text-right tabular">৳ {r.balance.toFixed(2)}</td>
                    <td className="px-4 py-3 text-right tabular">
                      {isEditing ? (
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={editing.required}
                          onChange={(e) => setEditing({ ...editing, required: e.target.value })}
                          className="w-28 h-8 px-2 border border-gray-300 rounded-sm text-right text-sm tabular"
                        />
                      ) : (
                        <>৳ {r.turnover_required.toFixed(2)}</>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right tabular">
                      {isEditing ? (
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={editing.completed}
                          onChange={(e) => setEditing({ ...editing, completed: e.target.value })}
                          className="w-28 h-8 px-2 border border-gray-300 rounded-sm text-right text-sm tabular"
                        />
                      ) : (
                        <>৳ {r.turnover_completed.toFixed(2)}</>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right tabular font-semibold">
                      {r.turnover_remaining > 0 ? (
                        <span className="text-amber-600">৳ {r.turnover_remaining.toFixed(2)}</span>
                      ) : (
                        <span className="text-emerald-600">০</span>
                      )}
                    </td>
                    <td className="px-4 py-3 w-48">
                      <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full ${r.can_withdraw ? "bg-emerald-500" : "bg-amber-500"}`}
                          style={{ width: `${Math.round(r.turnover_progress * 100)}%` }}
                        />
                      </div>
                      <div className="text-[11px] text-gray-500 mt-1 tabular">
                        {Math.round(r.turnover_progress * 100)}%
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {r.can_withdraw ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                          Eligible
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                          Pending
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-2">
                        {isEditing ? (
                          <>
                            <button
                              onClick={() => handleSave(r)}
                              className="h-8 px-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-sm text-xs inline-flex items-center gap-1"
                              data-testid={`turnover-save-${r.username}`}
                            >
                              <Check size={12} /> Save
                            </button>
                            <button
                              onClick={() => setEditing(null)}
                              className="h-8 px-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-sm text-xs inline-flex items-center gap-1"
                            >
                              <X size={12} /> Cancel
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() =>
                                setEditing({
                                  id: r.id,
                                  required: r.turnover_required,
                                  completed: r.turnover_completed,
                                })
                              }
                              className="h-8 px-3 border border-gray-200 hover:bg-gray-50 rounded-sm text-xs inline-flex items-center gap-1"
                              data-testid={`turnover-edit-${r.username}`}
                            >
                              <Edit3 size={12} /> Edit
                            </button>
                            <button
                              onClick={() => handleReset(r)}
                              className="h-8 px-3 border border-rose-200 text-rose-600 hover:bg-rose-50 rounded-sm text-xs inline-flex items-center gap-1"
                              data-testid={`turnover-reset-${r.username}`}
                            >
                              <RotateCcw size={12} /> Reset
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <Pagination total={paged.totalRows} page={paged.page} pageSize={paged.pageSize} onChange={paged.setPage} testIdPrefix="turnover-pg" />
      </div>
    </div>
  );
}

function SetField({ label, hint, value, onChange, testId, disabled }) {
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5">{label}</label>
      <input
        type="number"
        step="0.1"
        min="0"
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
        disabled={disabled}
        className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm tabular disabled:opacity-50 disabled:bg-gray-50"
        data-testid={testId}
      />
      {hint && <div className="text-[11px] text-gray-500 mt-1">{hint}</div>}
    </div>
  );
}

function StatCard({ label, value, tone }) {
  const toneMap = {
    slate: "bg-white border-gray-200",
    amber: "bg-amber-50 border-amber-200",
    emerald: "bg-emerald-50 border-emerald-200",
  };
  return (
    <div className={`border rounded-sm p-4 ${toneMap[tone] || toneMap.slate}`}>
      <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">{label}</div>
      <div className="text-2xl font-bold text-gray-900 mt-1 tabular">{value}</div>
    </div>
  );
}


/* ===================== Advanced (new turnover_system) ===================== */
function AdvancedTurnover() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [adjustUid, setAdjustUid] = useState("");
  const [adjustData, setAdjustData] = useState(null);
  const [adjForm, setAdjForm] = useState({ set_required: "", set_progress: "", note: "" });
  const [adjBusy, setAdjBusy] = useState(false);

  const reload = useCallback(async () => {
    try {
      const { data } = await api.get("/site/admin/turnover/config");
      setCfg(data);
    } catch (e) {
      toast.error("Failed to load advanced turnover config");
    }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const setField = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const setGc = (cat, val) => setCfg((c) => ({ ...c, game_contribution: { ...c.game_contribution, [cat]: Number(val) || 0 } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/site/admin/turnover/config", cfg);
      toast.success("Advanced turnover saved");
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const lookupUser = async () => {
    if (!adjustUid.trim()) return toast.error("Enter player ID");
    setAdjBusy(true);
    try {
      const { data } = await api.get(`/site/admin/turnover/users/${adjustUid.trim()}`);
      setAdjustData(data);
      setAdjForm({ set_required: String(data.required), set_progress: String(data.progress), note: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "User not found");
      setAdjustData(null);
    } finally {
      setAdjBusy(false);
    }
  };

  const adjust = async (forceComplete = false) => {
    if (!adjustUid) return;
    setAdjBusy(true);
    try {
      const body = forceComplete
        ? { force_complete: true, note: adjForm.note }
        : {
            set_required: adjForm.set_required === "" ? null : Number(adjForm.set_required),
            set_progress: adjForm.set_progress === "" ? null : Number(adjForm.set_progress),
            note: adjForm.note,
          };
      const { data } = await api.post(`/site/admin/turnover/users/${adjustUid.trim()}/adjust`, body);
      toast.success(forceComplete ? "Turnover force-completed" : "Adjustment saved");
      if (data?.snapshot) setAdjustData(data.snapshot);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Adjust failed");
    } finally {
      setAdjBusy(false);
    }
  };

  if (!cfg) return null;

  return (
    <div className="bg-white border border-gray-200 rounded-sm p-5 mb-5" data-testid="turnover-advanced-panel">
      <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-100">
        <Settings2 size={14} className="text-amber-600" />
        <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">Dual-Bucket Turnover V2 (Deposit 2× + Bonus 2× · Game Contribution)</div>
      </div>

      <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-sm text-xs text-blue-900">
        <b>How it works:</b> Every deposit creates a <b>Deposit Bucket</b> (real money × Deposit Multiplier) and every bonus credit creates a <b>Bonus Bucket</b> (bonus amount × Bonus Multiplier). Both buckets must be fully wagered (game contribution applies per bet) before withdrawal is allowed. Default is <b>2× for both buckets</b>.
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-sm cursor-pointer hover:bg-gray-50">
          <input type="checkbox" checked={!!cfg.enabled} onChange={(e) => setField("enabled", e.target.checked)} className="h-4 w-4" data-testid="adv-turnover-enabled" />
          <div>
            <div className="text-sm font-semibold text-gray-900">{cfg.enabled ? "Enabled" : "Disabled"}</div>
            <div className="text-xs text-gray-500">Master switch for dual-bucket turnover.</div>
          </div>
        </label>
        <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-sm cursor-pointer hover:bg-gray-50">
          <input type="checkbox" checked={!!cfg.block_withdrawal} onChange={(e) => setField("block_withdrawal", e.target.checked)} className="h-4 w-4" data-testid="adv-turnover-block" />
          <div>
            <div className="text-sm font-semibold text-gray-900">{cfg.block_withdrawal ? "Block withdrawal" : "Warn only"}</div>
            <div className="text-xs text-gray-500">If on, players cannot withdraw before turnover completes.</div>
          </div>
        </label>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5">Deposit Multiplier (×) — Real-money deposits</label>
          <input
            type="number" step="0.1" min="0"
            value={cfg.default_multiplier ?? 2}
            onChange={(e) => setField("default_multiplier", parseFloat(e.target.value) || 0)}
            className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm tabular"
            data-testid="adv-turnover-mult"
          />
          <p className="text-[11px] text-gray-500 mt-1">e.g. ৳1,000 deposit × {cfg.default_multiplier ?? 2} = ৳{(((cfg.default_multiplier ?? 2)) * 1000).toLocaleString()} wager required.</p>
        </div>
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5">Bonus Multiplier (×) — All bonuses (agent 7%, deposit bonus, VIP, etc.)</label>
          <input
            type="number" step="0.1" min="0"
            value={cfg.default_bonus_multiplier ?? 2}
            onChange={(e) => setField("default_bonus_multiplier", parseFloat(e.target.value) || 0)}
            className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm tabular"
            data-testid="adv-turnover-bonus-mult"
          />
          <p className="text-[11px] text-gray-500 mt-1">e.g. ৳100 bonus × {cfg.default_bonus_multiplier ?? 2} = ৳{(((cfg.default_bonus_multiplier ?? 2)) * 100).toLocaleString()} wager required.</p>
        </div>
      </div>

      <div className="mb-4">
        <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Game Contribution % (turnover progress per ৳1 stake)</div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {Object.entries(cfg.game_contribution || {}).map(([cat, pct]) => (
            <label key={cat} className="block">
              <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-1">{cat}</div>
              <div className="relative">
                <input type="number" step="1" min="0" max="100" value={pct}
                  onChange={(e) => setGc(cat, e.target.value)}
                  className="w-full h-9 pr-7 pl-2 border border-gray-200 rounded-sm text-sm tabular"
                  data-testid={`adv-gc-${cat}`} />
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">%</span>
              </div>
            </label>
          ))}
        </div>
      </div>

      <div className="flex justify-end mb-6">
        <button onClick={save} disabled={saving}
          className="h-10 px-5 bg-amber-500 hover:bg-amber-600 text-white rounded-sm text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50"
          data-testid="adv-turnover-save">
          <Save size={14} /> {saving ? "Saving..." : "Save Advanced Settings"}
        </button>
      </div>

      {/* Per-player adjuster */}
      <div className="border-t border-gray-100 pt-4">
        <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold mb-3">Per-Player Adjustment</div>
        <div className="flex gap-2 mb-3">
          <input type="text" placeholder="Player ID (Mongo ObjectId)" value={adjustUid}
            onChange={(e) => setAdjustUid(e.target.value)}
            className="flex-1 h-10 px-3 border border-gray-200 rounded-sm text-sm font-mono"
            data-testid="adv-adj-uid" />
          <button onClick={lookupUser} disabled={adjBusy}
            className="h-10 px-4 bg-gray-800 hover:bg-gray-900 text-white rounded-sm text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="adv-adj-lookup">
            <Search size={13} /> Lookup
          </button>
        </div>
        {adjustData && (
          <div className="border border-gray-200 rounded-sm p-3 bg-gray-50 space-y-3">
            {/* Dual-bucket breakdown */}
            {(adjustData.deposit || adjustData.bonus) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {adjustData.deposit && (
                  <div className="border border-blue-200 bg-white rounded-sm p-3" data-testid="adv-adj-deposit-bucket">
                    <div className="text-[10px] uppercase tracking-widest text-blue-700 font-bold mb-1.5">Deposit Bucket</div>
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div><span className="text-gray-500">Req:</span> <b className="tabular">৳{Number(adjustData.deposit.required).toLocaleString()}</b></div>
                      <div><span className="text-gray-500">Done:</span> <b className="tabular">৳{Number(adjustData.deposit.progress).toLocaleString()}</b></div>
                      <div><span className="text-gray-500">Left:</span> <b className={`tabular ${adjustData.deposit.completed ? "text-emerald-700" : "text-amber-700"}`}>৳{Number(adjustData.deposit.remaining).toLocaleString()}</b></div>
                    </div>
                    <div className={`text-[10px] mt-1 font-semibold ${adjustData.deposit.completed ? "text-emerald-600" : "text-amber-600"}`}>
                      {adjustData.deposit.completed ? "✓ Completed" : "⧗ Pending"}
                    </div>
                  </div>
                )}
                {adjustData.bonus && (
                  <div className="border border-purple-200 bg-white rounded-sm p-3" data-testid="adv-adj-bonus-bucket">
                    <div className="text-[10px] uppercase tracking-widest text-purple-700 font-bold mb-1.5">Bonus Bucket {adjustData.bonus.locked > 0 ? `· 🔒 ৳${Number(adjustData.bonus.locked).toLocaleString()} locked` : ""}</div>
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div><span className="text-gray-500">Req:</span> <b className="tabular">৳{Number(adjustData.bonus.required).toLocaleString()}</b></div>
                      <div><span className="text-gray-500">Done:</span> <b className="tabular">৳{Number(adjustData.bonus.progress).toLocaleString()}</b></div>
                      <div><span className="text-gray-500">Left:</span> <b className={`tabular ${adjustData.bonus.completed ? "text-emerald-700" : "text-amber-700"}`}>৳{Number(adjustData.bonus.remaining).toLocaleString()}</b></div>
                    </div>
                    <div className={`text-[10px] mt-1 font-semibold ${adjustData.bonus.completed ? "text-emerald-600" : "text-amber-600"}`}>
                      {adjustData.bonus.completed ? "✓ Completed" : "⧗ Pending"}
                    </div>
                  </div>
                )}
              </div>
            )}
            {/* Legacy combined view (kept for adjustment input compatibility) */}
            <div className="grid grid-cols-3 gap-3 text-sm border-t border-gray-200 pt-2">
              <div><span className="text-gray-500">Total Required:</span> <b className="tabular">৳{Number(adjustData.required).toLocaleString()}</b></div>
              <div><span className="text-gray-500">Total Progress:</span> <b className="tabular">৳{Number(adjustData.progress).toLocaleString()}</b></div>
              <div><span className="text-gray-500">Total Remaining:</span> <b className={`tabular ${adjustData.completed ? "text-emerald-700" : "text-amber-700"}`}>৳{Number(adjustData.remaining).toLocaleString()}</b></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <label className="block">
                <div className="text-[11px] text-gray-500 mb-1">Set Required (৳)</div>
                <input type="number" step="0.01" value={adjForm.set_required}
                  onChange={(e) => setAdjForm((s) => ({ ...s, set_required: e.target.value }))}
                  className="w-full h-9 px-2 border border-gray-200 rounded-sm text-sm tabular" />
              </label>
              <label className="block">
                <div className="text-[11px] text-gray-500 mb-1">Set Progress (৳)</div>
                <input type="number" step="0.01" value={adjForm.set_progress}
                  onChange={(e) => setAdjForm((s) => ({ ...s, set_progress: e.target.value }))}
                  className="w-full h-9 px-2 border border-gray-200 rounded-sm text-sm tabular" />
              </label>
              <label className="block">
                <div className="text-[11px] text-gray-500 mb-1">Note</div>
                <input type="text" value={adjForm.note}
                  onChange={(e) => setAdjForm((s) => ({ ...s, note: e.target.value }))}
                  className="w-full h-9 px-2 border border-gray-200 rounded-sm text-sm" />
              </label>
            </div>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => adjust(false)} disabled={adjBusy}
                className="h-9 px-4 bg-emerald-500 hover:bg-emerald-600 text-white rounded-sm text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"
                data-testid="adv-adj-save">
                <Save size={12} /> Save Adjustment
              </button>
              <button onClick={() => adjust(true)} disabled={adjBusy}
                className="h-9 px-4 bg-amber-500 hover:bg-amber-600 text-white rounded-sm text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"
                data-testid="adv-adj-force">
                <Check size={12} /> Force Complete
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
